import { useState, useEffect, useCallback, useRef } from "react";
import SvgIcon from "../../utils/Icon";
import apiClient from "../../utils/axiosPetition";
import salutioLogo from '../../images/salutioRed.png'; // Logo
import verificacionLogo from '../../images/verificacion.png'; // Logo de verificación Salutio
import { ReactComponent as Menu } from "../../assets/icons/menu.svg";
import { ReactComponent as Password } from "../../assets/icons/contraseña.svg";
import { ReactComponent as Notificacion } from "../../assets/icons/alarma.svg";
import { ReactComponent as Salir } from "../../assets/icons/salida.svg";
import { ReactComponent as Escritorio } from "../../assets/icons/escritorio.svg";
import { ReactComponent as Perfil } from "../../assets/icons/perfil.svg";
import { ReactComponent as Teleconsulta } from "../../assets/icons/agendar.svg";
import { ReactComponent as Citas } from "../../assets/icons/citas.svg";
import { ReactComponent as Documento } from "../../assets/icons/archivo.svg";
import { ReactComponent as Directorio } from "../../assets/icons/contacto.svg";
import { ReactComponent as Beneficios } from "../../assets/icons/beneficios.svg";
import { ReactComponent as SalutioRed } from "../../assets/icons/salutiored.svg";
import { ReactComponent as Usuario } from "../../assets/icons/usuario.svg";
import { ReactComponent as Chat } from "../../assets/icons/chat.svg";
import { useDataUser } from "../../context/dataUserContext";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faArrowRight, faExclamationCircle, faUserMd, faStore, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import { FaCrown } from "react-icons/fa";
import { useMembership } from "../../context/MembershipContext";
import ChangePasswordModal from "../GlobalComponents/ChangePasswordModal";
import DoctorAvailabilityReminderModal from "../DoctorComponent/DoctorAvailabilityReminderModal";
import DoctorMarketplaceReminderModal, { getMarketplaceReminderDismissedKey } from "../DoctorComponent/DoctorMarketplaceReminderModal";
import DoctorEpaycoOnboardingModal, { getEpaycoOnboardingDismissedKey } from "../DoctorComponent/DoctorEpaycoOnboardingModal";
import PatientProfileOnboardingModal, { getPatientProfileOnboardingDismissedKey } from "./PatientProfileOnboardingModal";
import { alertaGeneral } from "../../utils/alertas";
import moment from "moment-timezone";
import 'moment/locale/es';
import { parseAppointmentDate } from '../../utils/appointmentDate';

const NavbarPatientDoctor = () => {
    // false = barra estrecha (iconos); true = sidebar expandido fijo — abierto por defecto en desktop
    const [isSidebarOpen, setIsSidebarOpen] = useState(
        () => typeof window !== 'undefined' && window.innerWidth >= 768
    );
    const [userImageProfile, setUserImageProfile] = useState(() => localStorage.getItem('imageProfile'));
    const [imageProfileCacheKey, setImageProfileCacheKey] = useState(() => Date.now());
    const location = useLocation();
    const [showModalChangePassword, setShowModalChangePassword] = useState(false);
    const [isDoctorVerified, setIsDoctorVerified] = useState(false);
    const [doctorEstado, setDoctorEstado] = useState(null);
    // Hint post-OAuth/login para evitar flash de menú asistencial antes de getState
    const [doctorTipoPerfil, setDoctorTipoPerfil] = useState(() => localStorage.getItem('doctorTipoPerfil') || null);
    const [doctorPrestacionServicio, setDoctorPrestacionServicio] = useState(() => {
        const raw = localStorage.getItem('doctorPrestacionServicio');
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return null;
    });
    const [doctorStateLoaded, setDoctorStateLoaded] = useState(false);
    const [showAvailabilityReminder, setShowAvailabilityReminder] = useState(false);
    const [showMarketplaceReminder, setShowMarketplaceReminder] = useState(false);
    const [showEpaycoOnboarding, setShowEpaycoOnboarding] = useState(false);
    const [showPatientProfileOnboarding, setShowPatientProfileOnboarding] = useState(false);
    const [newAppointmentsCount, setNewAppointmentsCount] = useState(0);
    const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);
    const [isPatientProfileIncomplete, setIsPatientProfileIncomplete] = useState(false);
    const [hasPatientAgendadoCita, setHasPatientAgendadoCita] = useState(false);
    const pollingIntervalRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const availabilityReminderCheckedRef = useRef(false);

    const { isAuth, fullName, setIsAuth, role, doctorId, patientId } = useDataUser();
    const { isPremium, loading: membershipLoading, membershipStatus } = useMembership();
    const navigate = useNavigate();

    // Sincronizar foto de perfil del menú con localStorage (p. ej. tras OAuth o al actualizar en perfil)
    useEffect(() => {
        setUserImageProfile(localStorage.getItem('imageProfile'));
    }, [location.pathname]);

    // Actualizar foto del menú al instante cuando el usuario guarda una nueva (sin esperar al reload)
    useEffect(() => {
        const handleImageProfileUpdated = (e) => {
            if (e.detail) {
                setUserImageProfile(e.detail);
                setImageProfileCacheKey(Date.now());
            }
        };
        window.addEventListener('imageProfileUpdated', handleImageProfileUpdated);
        return () => window.removeEventListener('imageProfileUpdated', handleImageProfileUpdated);
    }, []);

    // Si el doctor no ha pagado la membresía (p. ej. se registró con Google en /registro-pagado), redirigir a registro-pagado
    useEffect(() => {
        if (role !== 'doctor' || !doctorId) return;
        if (location.pathname === '/registro-pagado' || location.pathname === '/registro-pagado-usuario') return;
        const checkPayment = async () => {
            try {
                const res = await apiClient.get('/api/afiliado/requires-membership-payment');
                if (res.data?.requiresMembershipPayment) {
                    navigate('/registro-pagado', { replace: true, state: { needsPayment: true, fromDoctorArea: true } });
                }
            } catch (err) {
                console.error('Error verificando pago de membresía:', err);
            }
        };
        checkPayment();
    }, [role, doctorId, location.pathname, navigate]);

    // Cerrar sidebar en móviles al hacer clic en un link
    const handleLinkClick = () => {
        if (window.innerWidth < 768) {
            setIsSidebarOpen(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        setIsAuth(false);
        navigate('/login');
    };

    const onHandleChangePassword = () => {
        setShowModalChangePassword(!showModalChangePassword);
    }

    // Función para contar nuevas citas no vistas
    const checkNewAppointmentsCount = useCallback(async () => {
        if (role !== 'doctor' || !doctorId) {
            setNewAppointmentsCount(0);
            return;
        }
        // Expertos no atienden citas clínicas
        if (doctorTipoPerfil === 'experto') {
            setNewAppointmentsCount(0);
            return;
        }

        try {
            // Obtener citas vistas desde localStorage
            const key = `viewedAppointments_${doctorId}`;
            const viewed = localStorage.getItem(key);
            const viewedAppointmentIds = viewed ? JSON.parse(viewed) : [];

            // Obtener todas las citas actuales
            const response = await apiClient.get(`/api/clinic/getAllAppointmentsDoctor/${doctorId}`);
            const currentAppointmentIds = response.data.map(app => app._id);
            
            // Contar nuevas citas
            const newIds = currentAppointmentIds.filter(id => !viewedAppointmentIds.includes(id));
            setNewAppointmentsCount(newIds.length);
        } catch (error) {
            console.error('Error verificando nuevas citas:', error);
            setNewAppointmentsCount(0);
        }
    }, [role, doctorId, doctorTipoPerfil]);

    // Verificar si el doctor está aprobado/verificado y si el perfil está incompleto
    useEffect(() => {
        const checkDoctorVerificationStatus = async () => {
            if (role === 'doctor' && doctorId) {
                setDoctorStateLoaded(false);
                availabilityReminderCheckedRef.current = false;
                try {
                    const response = await apiClient.get(`/api/afiliado/getState/${doctorId}`);
                    if (response.data) {
                        const state = response.data.state;
                        const tipo = response.data.tipoPerfil || null;
                        const prestacion = typeof response.data.prestacionServicio === 'boolean'
                            ? response.data.prestacionServicio
                            : null;
                        setDoctorEstado(state);
                        setDoctorTipoPerfil(tipo);
                        setDoctorPrestacionServicio(prestacion);
                        try {
                            if (tipo) localStorage.setItem('doctorTipoPerfil', tipo);
                            else localStorage.removeItem('doctorTipoPerfil');
                            if (typeof prestacion === 'boolean') {
                                localStorage.setItem('doctorPrestacionServicio', String(prestacion));
                            } else {
                                localStorage.removeItem('doctorPrestacionServicio');
                            }
                        } catch (e) { /* ignore */ }
                        if (state === 'aprobado') {
                            setIsDoctorVerified(true);
                            setIsProfileIncomplete(false);
                        } else if (state === 'pendiente_epayco') {
                            setIsDoctorVerified(false);
                            setIsProfileIncomplete(false);
                        } else {
                            // Si el estado es 'no_aprobado', 'en_revision', 'rechazado' o no existe, el perfil está incompleto
                            setIsDoctorVerified(false);
                            setIsProfileIncomplete(true);
                        }
                    } else {
                        // Si no hay respuesta, considerar el perfil como incompleto
                        setIsDoctorVerified(false);
                        setIsProfileIncomplete(true);
                    }
                } catch (error) {
                    console.log('Error verificando estado del doctor:', error);
                    // En caso de error, considerar el perfil como incompleto para mostrar el indicador
                    setIsDoctorVerified(false);
                    setIsProfileIncomplete(true);
                } finally {
                    setDoctorStateLoaded(true);
                }
            } else {
                setIsProfileIncomplete(false);
                setDoctorStateLoaded(false);
            }
        };

        checkDoctorVerificationStatus();
    }, [role, doctorId]);

    // Perfil experto (dijo No a servicios clínicos): no usa agenda/citas
    const esDoctorExperto =
        doctorTipoPerfil === 'experto' ||
        (isDoctorVerified && doctorPrestacionServicio === false && doctorTipoPerfil !== 'asistencial');
    // Hasta saber el tipo (getState), no asumir asistencial: evita menú/modal incorrectos tras login Google
    const esDoctorAsistencial = doctorStateLoaded && !esDoctorExperto;

    // Doctor verificado: recordatorio según flujo (citas vs marketplace)
    useEffect(() => {
        if (role !== 'doctor' || !doctorId || !isDoctorVerified || !doctorStateLoaded) {
            return;
        }
        if (availabilityReminderCheckedRef.current) {
            return;
        }

        if (esDoctorExperto) {
            // Nunca mostrar modal de agenda clínica a expertos
            setShowAvailabilityReminder(false);

            if (location.pathname.includes('mis-productos')) {
                availabilityReminderCheckedRef.current = true;
                return;
            }
            if (sessionStorage.getItem(getMarketplaceReminderDismissedKey(doctorId)) === '1') {
                availabilityReminderCheckedRef.current = true;
                return;
            }

            let cancelled = false;

            // Solo mostrar si aún no tiene ePayco marketplace confirmado
            apiClient.get('/api/marketplace/vendedor/estado')
                .then((res) => {
                    if (cancelled) return;
                    availabilityReminderCheckedRef.current = true;
                    const habilitado = Boolean(res.data?.habilitadoParaVender);
                    setShowMarketplaceReminder(!habilitado);
                })
                .catch(() => {
                    if (cancelled) return;
                    availabilityReminderCheckedRef.current = true;
                    setShowMarketplaceReminder(false);
                });

            return () => { cancelled = true; };
        }

        if (location.pathname.includes('configuracion-citas')) {
            return;
        }

        availabilityReminderCheckedRef.current = true;
        setShowMarketplaceReminder(false);
        let cancelled = false;

        apiClient.get(`/api/doctor/horarios/${doctorId}`)
            .then((res) => {
                if (cancelled) return;
                const tieneHorarios = Array.isArray(res.data?.horarios) && res.data.horarios.length > 0;
                setShowAvailabilityReminder(!tieneHorarios);
            })
            .catch(() => {
                if (!cancelled) setShowAvailabilityReminder(false);
            });

        return () => {
            cancelled = true;
        };
    }, [role, doctorId, isDoctorVerified, doctorStateLoaded, location.pathname, esDoctorExperto]);

    // Doctor pendiente ePayco: modal guía a Configuración de citas (una vez por sesión)
    useEffect(() => {
        if (role !== 'doctor' || !doctorId || doctorEstado !== 'pendiente_epayco') {
            return;
        }
        if (location.pathname.includes('configuracion-citas')) {
            return;
        }
        const dismissedKey = getEpaycoOnboardingDismissedKey(doctorId);
        if (sessionStorage.getItem(dismissedKey) === '1') {
            return;
        }
        setShowEpaycoOnboarding(true);
    }, [role, doctorId, doctorEstado, location.pathname]);

    // Verificar si el paciente tiene el perfil incompleto (para resaltar el botón Perfil)
    useEffect(() => {
        const checkPatientProfileComplete = async () => {
            if (role === 'patient' && patientId) {
                try {
                    const response = await apiClient.get(`/api/patient/${patientId}`);
                    const profileComplete = response.data?.profileComplete === true;
                    setIsPatientProfileIncomplete(!profileComplete);
                } catch (error) {
                    // Si falla la petición o no hay datos, considerar perfil incompleto
                    const fromStorage = localStorage.getItem('patientProfileComplete');
                    setIsPatientProfileIncomplete(fromStorage !== 'true');
                }
            } else {
                setIsPatientProfileIncomplete(false);
            }
        };

        checkPatientProfileComplete();
    }, [role, patientId]);

    // Paciente con perfil incompleto: modal guía al completar perfil (una vez por sesión)
    useEffect(() => {
        if (role !== 'patient' || !patientId || !isPatientProfileIncomplete) {
            setShowPatientProfileOnboarding(false);
            return;
        }
        if (location.pathname.includes('/patient/perfil')) {
            setShowPatientProfileOnboarding(false);
            return;
        }
        const dismissedKey = getPatientProfileOnboardingDismissedKey(patientId);
        if (sessionStorage.getItem(dismissedKey) === '1') {
            return;
        }
        setShowPatientProfileOnboarding(true);
    }, [role, patientId, isPatientProfileIncomplete, location.pathname]);

    // Resaltar Perfil cuando doctor o paciente tienen perfil incompleto
    const showProfileHighlight = (role === 'doctor' && isProfileIncomplete) || (role === 'patient' && isPatientProfileIncomplete);

    // Funciones para manejar citas vistas en localStorage (para pacientes)
    const getViewedAppointmentsPatient = useCallback(() => {
        if (!patientId) return [];
        const key = `viewedAppointments_patient_${patientId}`;
        const viewed = localStorage.getItem(key);
        return viewed ? JSON.parse(viewed) : [];
    }, [patientId]);

    const saveViewedAppointmentsPatient = useCallback((appointmentIds) => {
        if (!patientId) return;
        const key = `viewedAppointments_patient_${patientId}`;
        localStorage.setItem(key, JSON.stringify(appointmentIds));
    }, [patientId]);

    // Función para detectar nuevas citas y mostrar notificación (para pacientes)
    const checkForNewPatientAppointments = useCallback(async (newAppointments, previousAppointmentIds) => {
        const newAppointmentIds = newAppointments.map(app => app._id);
        const newIds = newAppointmentIds.filter(id => !previousAppointmentIds.includes(id));

        if (newIds.length > 0) {
            const newAppointmentsList = newAppointments.filter(app => newIds.includes(app._id));
            
            // Si hay múltiples nuevas citas, mostrar una notificación con todas
            if (newAppointmentsList.length === 1) {
                const appointment = newAppointmentsList[0];
                const doctorName = appointment.doctor?.fullName || 'Doctor';
                const appointmentDate = parseAppointmentDate(appointment.date).format('DD/MM/YYYY');
                const appointmentTime = parseAppointmentDate(appointment.date).format('hh:mm A');
                
                const result = await alertaGeneral({
                    titulo: 'Nueva cita agendada',
                    messageHtml: `
                        <div style="text-align: left;">
                            <p><strong>Doctor:</strong> ${doctorName}</p>
                            <p><strong>Fecha:</strong> ${appointmentDate}</p>
                            <p><strong>Hora:</strong> ${appointmentTime}</p>
                            ${appointment.notes ? `<p><strong>Notas:</strong> ${appointment.notes}</p>` : ''}
                        </div>
                    `,
                    isInfo: true,
                    confirmButtonText: 'Ver mis citas'
                });

                // Si el usuario hace clic en "Ver mis citas", redirigir a teleconsultas
                if (result.isConfirmed) {
                    navigate('/patient/teleconsultas');
                }
            } else {
                // Múltiples nuevas citas
                const appointmentsList = newAppointmentsList.map(app => {
                    const doctorName = app.doctor?.fullName || 'Doctor';
                    const appointmentDate = parseAppointmentDate(app.date).format('DD/MM/YYYY');
                    const appointmentTime = parseAppointmentDate(app.date).format('hh:mm A');
                    return `<li style="margin-bottom: 10px;">
                        <strong>${doctorName}</strong><br/>
                        ${appointmentDate} a las ${appointmentTime}
                    </li>`;
                }).join('');
                
                const result = await alertaGeneral({
                    titulo: `${newAppointmentsList.length} nuevas citas agendadas`,
                    messageHtml: `
                        <div style="text-align: left;">
                            <ul style="list-style: none; padding: 0;">
                                ${appointmentsList}
                            </ul>
                        </div>
                    `,
                    isInfo: true,
                    confirmButtonText: 'Ver mis citas'
                });

                if (result.isConfirmed) {
                    navigate('/patient/teleconsultas');
                }
            }
            
            // Marcar las nuevas citas como vistas después de mostrar la notificación
            const viewedIds = getViewedAppointmentsPatient();
            const updatedViewedIds = [...new Set([...viewedIds, ...newIds])];
            saveViewedAppointmentsPatient(updatedViewedIds);
        }
    }, [navigate, getViewedAppointmentsPatient, saveViewedAppointmentsPatient]);

    // Función para verificar nuevas citas de pacientes
    const checkPatientAppointments = useCallback(async (isPolling = false) => {
        if (role !== 'patient' || !patientId) {
            return;
        }

        try {
            const response = await apiClient.get(`/api/clinic/getAllAppointmentsPatient/${patientId}`);
            
            const allAppointmentsReturn = response.data.map(appointment => ({
                ...appointment,
                date: appointment.date,
                service: appointment.doctor?.service ? 
                    appointment.doctor.service.replace(/\d+/g, '').replace(/_/g, ' ') : 
                    (appointment.service || 'Servicio no disponible'),
            }));

            setHasPatientAgendadoCita(allAppointmentsReturn.length > 0);

            // Identificar citas nuevas
            const previousAppointmentIds = getViewedAppointmentsPatient();
            const currentAppointmentIds = allAppointmentsReturn.map(app => app._id);
            const newIds = currentAppointmentIds.filter(id => !previousAppointmentIds.includes(id));
            
            // Si es polling y no es la carga inicial, verificar nuevas citas y mostrar notificación
            if (isPolling && !isInitialLoadRef.current && newIds.length > 0) {
                await checkForNewPatientAppointments(allAppointmentsReturn, previousAppointmentIds);
            }
            
            // Marcar que la carga inicial ya se completó
            if (isInitialLoadRef.current) {
                isInitialLoadRef.current = false;
                // Marcar todas las citas actuales como vistas en la carga inicial
                saveViewedAppointmentsPatient(currentAppointmentIds);
            }
        } catch (error) {
            console.error('Error verificando citas del paciente:', error);
        }
    }, [role, patientId, getViewedAppointmentsPatient, saveViewedAppointmentsPatient, checkForNewPatientAppointments]);

    // Verificar nuevas citas al montar y periódicamente
    useEffect(() => {
        if (role === 'doctor' && doctorId) {
            checkNewAppointmentsCount();
            
            // Polling cada 30 segundos para actualizar el contador
            const interval = setInterval(() => {
                checkNewAppointmentsCount();
            }, 30000);

            return () => clearInterval(interval);
        } else if (role === 'patient' && patientId) {
            // Carga inicial
            checkPatientAppointments(false);
            
            // Polling cada 30 segundos para detectar nuevas citas
            pollingIntervalRef.current = setInterval(() => {
                checkPatientAppointments(true);
            }, 30000);

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
            };
        } else {
            setNewAppointmentsCount(0);
        }
    }, [role, doctorId, patientId, checkNewAppointmentsCount, checkPatientAppointments]);

    // Lógica de saludo por rol
    const roleNormalized = (role || '').toLowerCase().trim();
    let saludo = 'Bienvenido a Redplika';
    if (roleNormalized === 'doctor') saludo = 'Bienvenido Miembro';
    else if (roleNormalized === 'patient') saludo = 'Bienvenido Usuario';
    else if (roleNormalized === 'admin') saludo = 'Bienvenido Administrador';
    else if (roleNormalized === 'clinic') saludo = 'Bienvenido/a Clínica';

    const sidebarLabelVisibility = isSidebarOpen
        ? '[&_nav_span]:opacity-100 [&_nav_span]:max-w-[11rem] [&_.profile-sidebar-text]:opacity-100 [&_.sidebar-footer-text]:opacity-100 [&_.sidebar-footer-text]:max-w-[8rem]'
        : '[&_nav_span]:md:opacity-0 [&_nav_span]:md:max-w-0 [&_.profile-sidebar-text]:md:opacity-0 [&_.sidebar-footer-text]:md:opacity-0 [&_.sidebar-footer-text]:md:max-w-0';
    const sidebarNavLinkLayout = isSidebarOpen ? 'px-5 justify-start' : 'px-5 md:px-3 md:justify-center';
    const sidebarNavIconMr = isSidebarOpen ? 'mr-2' : 'mr-2 md:mr-0';
    const sidebarNavIconMr3 = isSidebarOpen ? 'mr-3' : 'mr-3 md:mr-0';

    return (
        <div className="flex h-screen max-h-screen overflow-hidden bg-[#f0f0f0]">
            {/* Overlay para móviles */}
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-[55] md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar fijo: cerrado = rail de iconos; abierto = panel completo */}
            <div className={`
                flex flex-col bg-[#205087ff] overflow-hidden
                fixed top-0 left-0 h-screen z-[60]
                transition-all duration-300 ease-in-out
                ${sidebarLabelVisibility}
                ${isSidebarOpen ? 'w-64 max-w-[16rem] translate-x-0' : 'w-20 max-w-[5rem] -translate-x-full md:translate-x-0'}
            `}>
                <div className={`flex flex-col items-center justify-center px-2 pb-4 pt-4 bg-gradient-to-b from-[#1f94c2ff] to-[#205087ff] border-b border-white/10 overflow-hidden transition-all duration-300 ${role === 'doctor' && isDoctorVerified ? 'min-h-[190px]' : 'min-h-[170px]'} ${isSidebarOpen ? '' : 'md:min-h-[100px]'}`}>
                    <div className="relative flex shrink-0" style={{ marginBottom: role === 'doctor' && isDoctorVerified ? 4 : 0 }}>
                        <div className="absolute -inset-1 rounded-full bg-white/20 blur-sm group-hover:bg-white/30 transition-all pointer-events-none" />
                        <div className={`relative w-20 h-20 rounded-full ring-4 ring-white/40 ring-offset-2 ring-offset-[#205087ff] overflow-hidden bg-white shadow-xl flex items-center justify-center transition-all duration-300 ${isSidebarOpen ? '' : 'md:w-14 md:h-14'}`}>
                            {userImageProfile ? (
                                <img 
                                    src={`${userImageProfile}${userImageProfile.includes('?') ? '&' : '?'}t=${imageProfileCacheKey}`}
                                    alt="Foto de perfil" 
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                />
                            ) : (
                                <SvgIcon Icon={Usuario} className="w-12 h-12 md:w-14 md:h-14 text-[#205087ff]" />
                            )}
                        </div>
                        {role === 'doctor' && isDoctorVerified && (
                            <div className="absolute bottom-0 right-0 w-7 h-7 md:w-8 md:h-8 bg-white rounded-full p-1 shadow-lg ring-2 ring-emerald-400 flex items-center justify-center">
                                <img src={verificacionLogo} alt="Verificado" className="w-full h-full object-contain" title="Doctor verificado por Redplika" />
                            </div>
                        )}
                    </div>
                    <div className="mt-3 w-full px-1 flex flex-col items-center gap-1.5 overflow-hidden">
                        <p className="profile-sidebar-text text-white font-semibold text-center text-xs md:text-sm leading-tight line-clamp-2 max-w-full transition-opacity duration-200 whitespace-nowrap truncate" title={fullName}>
                            {fullName || 'Usuario'}
                        </p>
                        {role === 'doctor' && isDoctorVerified && (
                            <span className="profile-sidebar-text inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs font-medium shadow-inner transition-opacity duration-200">
                                <svg className="w-4 h-4 shrink-0 text-emerald-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Verificado
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col flex-1">
                    <nav className="flex-1 text-wrap overflow-y-auto">
                        <Link to="dashboard" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                            <SvgIcon Icon={Escritorio} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                            <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Escritorio </span>
                        </Link>
                        <Link 
                            to="perfil" 
                            onClick={handleLinkClick} 
                            className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200 relative overflow-hidden min-w-0 ${
                                showProfileHighlight
                                    ? isSidebarOpen
                                        ? 'bg-[#f79865ff]/30 border-l-4 border-[#f79865ff]'
                                        : 'border-l-4 border-[#f79865ff] md:border-l-0'
                                    : ''
                            }`}
                        >
                            {showProfileHighlight && (
                                <>
                                    <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-[#f79865ff]/40 to-transparent animate-[shimmer_2s_infinite] -translate-x-full transition-opacity duration-200 pointer-events-none ${isSidebarOpen ? 'opacity-100' : 'opacity-0 md:opacity-0'}`} />
                                    <div className={`absolute left-0 top-0 bottom-0 w-1 bg-[#f79865ff] shadow-lg shadow-[#f79865ff]/50 transition-opacity duration-200 pointer-events-none ${isSidebarOpen ? 'opacity-100 animate-pulse' : 'opacity-0 md:opacity-0'}`} />
                                </>
                            )}
                            <SvgIcon Icon={Perfil} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200 ${showProfileHighlight && isSidebarOpen ? 'animate-bounce text-[#f79865ff]' : showProfileHighlight ? 'text-[#f79865ff]' : ''}`} />
                            <span className={`w-full break-words whitespace-nowrap overflow-hidden transition-all duration-200 ${showProfileHighlight ? 'font-bold' : ''}`}> Perfil </span>
                            {showProfileHighlight && (
                                <div className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 transition-opacity duration-200 pointer-events-none ${isSidebarOpen ? 'opacity-100' : 'opacity-100 md:opacity-0'}`}>
                                    <div className="relative">
                                        <FontAwesomeIcon 
                                            icon={faExclamationCircle} 
                                            className="w-5 h-5 text-[#f79865ff] animate-pulse drop-shadow-lg" 
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#f79865ff] opacity-75 animate-ping"></span>
                                        </span>
                                    </div>
                                    <FontAwesomeIcon 
                                        icon={faArrowRight} 
                                        className="w-3.5 h-3.5 text-[#f79865ff] animate-[slide_1s_ease-in-out_infinite] drop-shadow-md" 
                                    />
                                </div>
                            )}
                        </Link>
                        {/* Estilos de animación personalizados */}
                        {showProfileHighlight && (
                            <style>{`
                                @keyframes shimmer {
                                    0% { transform: translateX(-100%); }
                                    100% { transform: translateX(200%); }
                                }
                                @keyframes slide {
                                    0%, 100% { transform: translateX(0); }
                                    50% { transform: translateX(4px); }
                                }
                            `}</style>
                        )}
                        {/* <Link to="notificaciones" onClick={handleLinkClick} className="flex w-full items-center px-5 py-4 font-medium text-wrap text-white hover:text-[#205087ff] hover:bg-[#f79865ff]">
                            <SvgIcon Icon={Notificacion} className={"w-5 h-5 mr-2 shrink-0 cursor-pointer"} />
                            <span className="w-full break-words whitespace-normal"> Notificaciones </span>
                        </Link> */}
                        {/* Teleconsultas: pacientes y doctores (también expertos, pueden agendar como pacientes) */}
                        <Link to="teleconsultas" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                            <SvgIcon Icon={Teleconsulta} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                            <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Teleconsultas </span>
                        </Link>
                        {role === 'patient' && (
                            <Link to="documentos-asignados" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                                <SvgIcon Icon={Documento} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Documentos médicos </span>
                            </Link>
                        )}
                        {/* Solo doctor asistencial (presta servicios clínicos). Esperar getState para no flashar menú incorrecto. */}
                        {role === 'doctor' && esDoctorAsistencial && (
                            <Link to="configuracion-citas" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200 relative`}>
                                <SvgIcon Icon={Citas} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Configuración citas </span>
                                {newAppointmentsCount > 0 && (
                                    <span className={`absolute top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse transition-all duration-200 ${isSidebarOpen ? 'right-2' : 'right-2 md:right-1'}`}>
                                        {newAppointmentsCount > 9 ? '9+' : newAppointmentsCount}
                                    </span>
                                )}
                            </Link>
                        )}
                        {role === 'doctor' && isDoctorVerified && esDoctorAsistencial && (
                            <Link to="documentos-asignados" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                                <SvgIcon Icon={Documento} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Documentos asignados </span>
                            </Link>
                        )}
                        {role === 'doctor' && isDoctorVerified && esDoctorAsistencial && (
                            <div className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200 cursor-not-allowed opacity-75`}>
                                <SvgIcon Icon={Chat} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200">
                                    Juntas Interdisciplinarias <span className="text-xs text-yellow-200 ml-2">(En desarrollo)</span>
                                </span>
                            </div>
                        )}
                        <Link
                            to="directorio-profesionales"
                            onClick={handleLinkClick}
                            className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200 min-w-0 ${
                                role === 'patient' && !hasPatientAgendadoCita
                                    ? isSidebarOpen
                                        ? 'bg-[#f79865ff]/90 animate-pulse ring-2 ring-amber-400 ring-offset-2 ring-offset-[#205087ff] rounded-md md:rounded-none'
                                        : 'md:bg-transparent md:ring-0 bg-[#f79865ff]/90 animate-pulse ring-2 ring-amber-400 ring-offset-2 ring-offset-[#205087ff] rounded-md md:rounded-none'
                                    : ''
                            }`}
                        >
                            <SvgIcon Icon={Directorio} className={`w-5 h-5 ${sidebarNavIconMr3} shrink-0 cursor-pointer transition-all duration-200`} />
                            <span className="w-full break-words whitespace-nowrap overflow-hidden transition-all duration-200"> Directorio profesional </span>
                        </Link>
                        <Link to="aliados" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                            <SvgIcon Icon={Beneficios} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                            <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Beneficios / Aliados </span>
                        </Link>
                        <Link to="redplikaplace" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                            <FontAwesomeIcon icon={faStore} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                            <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> REDPLIKAPLACE </span>
                        </Link>
                        {/* Link únicamente para doctor: gestión de sus productos en el marketplace */}
                        {role === 'doctor' && (
                            <Link to="mis-productos" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                                <FontAwesomeIcon icon={faBoxOpen} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Mis productos </span>
                            </Link>
                        )}
                        {/* Link únicamente para paciente */}
                        {role === 'patient' && !membershipLoading && membershipStatus && !isPremium() && (
                            <Link to="miembro" onClick={handleLinkClick} className={`flex w-full items-center py-4 ${sidebarNavLinkLayout} font-medium text-white hover:bg-[#f79865ff] hover:text-white transition-all duration-200`}>
                                <FontAwesomeIcon icon={faUserPlus} className={`w-5 h-5 ${sidebarNavIconMr} shrink-0 cursor-pointer transition-all duration-200`} />
                                <span className="w-full break-words whitespace-normal overflow-hidden whitespace-nowrap transition-all duration-200"> Quiero ser miembro </span>
                            </Link>
                        )}
                    </nav>
                    <div className="flex relative items-center justify-center py-4 mt-auto overflow-hidden">
                        <span className="text-white text-xs text-center flex flex-col items-center">
                            <SvgIcon Icon={SalutioRed} className="w-7 h-7 text-center mx-auto mb-1 shrink-0" />
                            <span className="sidebar-footer-text overflow-hidden whitespace-nowrap transition-all duration-200">Redplika©2025</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Contenido Principal */}
            <div className={`flex flex-col flex-1 min-h-0 overflow-y-auto bg-[#f0f0f0] pb-20 md:pb-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
                {/* Navbar */}
                <div className="sticky top-0 flex items-center justify-between bg-white px-3 py-2 z-50 md:z-50 shrink-0 shadow-sm border-b border-slate-200">
                    {/* Boton Menu hamburguesa + Logo */}
                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="active:outline-none focus:outline-none bg-[#1f94c2ff] p-1.5 rounded-md">
                            <SvgIcon Icon={Menu} className="text-white w-4 h-4 active:outline-none focus:outline-none transform hover:scale-110" />
                        </button>
                        <img
                            src={salutioLogo}
                            alt="Logo"
                            className="h-7 md:h-8 w-auto object-contain"
                        />
                    </div>

                    {/* Saludo y badge (barra blanca) */}
                    <div className="flex items-center space-x-2 md:space-x-4 text-sm">
                        <div className="hidden sm:flex items-center gap-2 flex-wrap">
                            {role === 'doctor' ? (
                                <div className="inline-flex items-center gap-2.5 pl-3 pr-3.5 py-1.5 rounded-lg bg-slate-50 border border-slate-100">
                                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1f94c2]/10 text-[#1f94c2]">
                                        <FontAwesomeIcon icon={faUserMd} className="w-4 h-4" />
                                    </span>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-slate-400 font-medium leading-tight">Área profesional</span>
                                        <span className="text-sm font-semibold text-slate-800 leading-tight">{saludo}</span>
                                    </div>
                                    {!membershipLoading && isPremium() && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold ml-0.5">
                                            <FaCrown className="w-3 h-3 text-amber-500" />
                                            Premium
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="text-[#1f94c2] font-medium text-sm md:text-base">{saludo}</span>
                            )}
                        </div>

                        <div className="relative group" hidden={!isAuth}>
                            <SvgIcon Icon={Password} className="text-[#205087ff] w-6 h-6 cursor-pointer transform hover:scale-110 shrink-0"
                                onClick={onHandleChangePassword}
                            />
                            <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-max bg-[#1f94c2ff] text-white text-xs rounded p-1 opacity-0 group-hover:opacity-100 transition">
                                Cambiar contraseña
                            </span>
                        </div>

                        {/* <SvgIcon Icon={Notificacion} className="text-[#205087ff] w-6 h-6 cursor-pointer transform hover:scale-110 shrink-0" /> */}

                        <span className="border-r border-2 border-[#f79865ff] h-10" />

                        <div className="flex items-center cursor-pointer transform hover:scale-110" onClick={handleLogout}>
                            <SvgIcon Icon={Salir} className="text-[#205087ff] w-6 h-6 mr-2 shrink-0" />
                            <span className={`text-[#1f94c2ff] ${isSidebarOpen ? 'hidden lg:flex' : ''} text-base md:text-md`}> {isAuth ? 'Salir' : 'Iniciar Sesion'} </span>
                        </div>
                    </div>
                </div>
                {/* Contenido: full width en marketplace; resto con ancho máximo */}
                <main className={`flex-1 min-h-[calc(100vh-4rem)] bg-[#f0f0f0] p-4 flex flex-col ${
                    location.pathname.includes('redplikaplace') || location.pathname.includes('mis-productos')
                        ? 'items-stretch'
                        : 'items-center'
                }`}>
                    <section className={`w-full px-2 md:px-4 ${
                        location.pathname.includes('redplikaplace') || location.pathname.includes('mis-productos')
                            ? ''
                            : 'max-w-6xl 2xl:max-w-7xl mx-auto'
                    }`}>
                        <Outlet />
                    </section>
                </main>
            </div>

            {/* Barra inferior tipo WhatsApp (solo móviles) */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
                <div className="flex items-stretch justify-around h-16 max-h-16">
                    <Link
                        to="dashboard"
                        onClick={handleLinkClick}
                        className={`flex flex-col flex-1 items-center justify-center gap-0.5 min-w-0 py-2 transition-colors ${
                            location.pathname.includes('dashboard') && !location.pathname.includes('directorio')
                                ? 'text-[#1f94c2ff]'
                                : 'text-slate-500'
                        }`}
                    >
                        <SvgIcon Icon={Escritorio} className="w-6 h-6 shrink-0" />
                        <span className="text-[10px] font-medium leading-tight">Escritorio</span>
                    </Link>
                    <Link
                        to="perfil"
                        onClick={handleLinkClick}
                        className={`flex flex-col flex-1 items-center justify-center gap-0.5 min-w-0 py-2 transition-colors ${
                            location.pathname.includes('perfil') ? 'text-[#1f94c2ff]' : 'text-slate-500'
                        }`}
                    >
                        <SvgIcon Icon={Perfil} className="w-6 h-6 shrink-0" />
                        <span className="text-[10px] font-medium leading-tight">Perfil</span>
                    </Link>
                    <Link
                        to="teleconsultas"
                        onClick={handleLinkClick}
                        className={`flex flex-col flex-1 items-center justify-center gap-0.5 min-w-0 py-2 transition-colors ${
                            location.pathname.includes('teleconsultas') ? 'text-[#1f94c2ff]' : 'text-slate-500'
                        }`}
                    >
                        <SvgIcon Icon={Teleconsulta} className="w-6 h-6 shrink-0" />
                        <span className="text-[10px] font-medium leading-tight">Citas</span>
                    </Link>
                    <Link
                        to="directorio-profesionales"
                        onClick={handleLinkClick}
                        className={`flex flex-col flex-1 items-center justify-center gap-0.5 min-w-0 py-2 transition-colors ${
                            location.pathname.includes('directorio') ? 'text-[#1f94c2ff]' : 'text-slate-500'
                        }`}
                    >
                        <SvgIcon Icon={Directorio} className="w-6 h-6 shrink-0" />
                        <span className="text-[10px] font-medium leading-tight">Directorio</span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setIsSidebarOpen(true)}
                        className={`flex flex-col flex-1 items-center justify-center gap-0.5 min-w-0 py-2 transition-colors ${
                            isSidebarOpen ? 'text-[#1f94c2ff]' : 'text-slate-500'
                        }`}
                        aria-label="Abrir menú"
                    >
                        <SvgIcon Icon={Menu} className="w-6 h-6 shrink-0" />
                        <span className="text-[10px] font-medium leading-tight">Más</span>
                    </button>
                </div>
            </nav>

            {/* Modal cambio de password */}
            {isAuth && showModalChangePassword && (
                <ChangePasswordModal
                    showModalChangePassword={showModalChangePassword}
                    setShowModalChangePassword={setShowModalChangePassword}
                />
            )}

            {role === 'doctor' && showAvailabilityReminder && esDoctorAsistencial && (
                <DoctorAvailabilityReminderModal onClose={() => setShowAvailabilityReminder(false)} />
            )}

            {role === 'doctor' && showMarketplaceReminder && esDoctorExperto && doctorStateLoaded && (
                <DoctorMarketplaceReminderModal
                    doctorId={doctorId}
                    onClose={() => setShowMarketplaceReminder(false)}
                />
            )}

            {role === 'doctor' && showEpaycoOnboarding && esDoctorAsistencial && (
                <DoctorEpaycoOnboardingModal
                    doctorId={doctorId}
                    onClose={() => setShowEpaycoOnboarding(false)}
                />
            )}

            {role === 'patient' && showPatientProfileOnboarding && (
                <PatientProfileOnboardingModal
                    patientId={patientId}
                    onClose={() => setShowPatientProfileOnboarding(false)}
                />
            )}
        </div>
    );

};

export default NavbarPatientDoctor;